import axios from "axios";

// Use a RELATIVE base so the browser treats every API call as same-origin.
// The frontend is served from the same hostname as the backend via the
// Kubernetes ingress (`/api/*` is proxied to FastAPI on :8001). Relative URLs
// eliminate cross-origin cookie problems entirely.
export const API_BASE = "/api";

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Coerce ANY thrown value (axios error, fetch error, raw object, string,
// undefined) into a human-readable string suitable for toast.error / banners.
// This function is the SINGLE source of truth for error display — never call
// toast.error(err) directly with a raw error object, or you'll get the dreaded
// "[object Object]" toast.
export function formatApiError(err) {
  if (err == null) return "Something went wrong.";
  if (typeof err === "string") return err;

  // Axios shape: err.response.data may be a string OR an object.
  const data = err?.response?.data;

  // 1) FastAPI structured-detail (the most common case):
  //    detail: "string"  → that string
  //    detail: { message, error, ... }  → prefer message, then error
  //    detail: [{ msg, type, ... }]     → join the msgs (Pydantic validation)
  if (data && data.detail != null) {
    const d = data.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
      const parts = d
        .map((it) => {
          if (it == null) return "";
          if (typeof it === "string") return it;
          if (typeof it.msg === "string") return it.msg;
          if (typeof it.message === "string") return it.message;
          try {
            return JSON.stringify(it);
          } catch {
            return "";
          }
        })
        .filter(Boolean);
      return parts.length ? parts.join(" ") : "Validation failed.";
    }
    if (typeof d === "object") {
      if (typeof d.message === "string" && d.message) return d.message;
      if (typeof d.error === "string" && d.error) return d.error;
      try {
        return JSON.stringify(d);
      } catch {
        return "Request failed.";
      }
    }
  }

  // 2) Some endpoints (mostly older ones) bypass `detail` and return a
  //    flat top-level shape like {error: "...", message: "..."}.
  if (data && typeof data === "object") {
    if (typeof data.message === "string" && data.message) return data.message;
    if (typeof data.error === "string" && data.error) return data.error;
  }
  if (typeof data === "string" && data) return data;

  // 3) Network / axios envelope errors.
  if (typeof err?.message === "string" && err.message) return err.message;

  // 4) Last resort — never `String(obj)` (that's the "[object Object]" trap).
  try {
    return JSON.stringify(err);
  } catch {
    return "Something went wrong.";
  }
}

export default api;
